/**
 * Formatting utilities for consistent data display across the application.
 * These functions ensure uniform formatting for fees, program names, and other text elements.
 */

/**
 * Format fee for display as '00,000 PKR' with comma separators and currency suffix.
 * @param {number|string} feeValue - Fee value (can be number or string)
 * @returns {string} Formatted fee string (e.g., '600,000 PKR') or 'Contact'
 */
export const formatFeeDisplay = (feeValue, feeType) => {
  if (feeValue === null || feeValue === undefined || feeValue === '') {
    return '—';
  }

  const str = String(feeValue).trim();
  if (!str) return '—';

  const lower = str.toLowerCase();
  if (['contact', 'n/a', 'na', 'tba', 'tbd', 'unknown', 'none', '-'].includes(lower)) {
    return '—';
  }

  // Descriptive fee statuses
  if (['free', 'waived', 'at actuals', 'included in tuition', 'varies by program'].includes(lower)) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Determine standard unit suffix from feeType if provided
  let defaultUnit = '';
  if (feeType) {
    const t = feeType.toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ').trim();
    if (t === 'credit hour' || t === 'per credit hour' || t === 'credit_hour') {
      defaultUnit = '/ credit hour';
    } else if (t === 'course' || t === 'per course' || t === 'per_course') {
      defaultUnit = '/ course';
    } else if (t === 'semester' || t === 'per semester') {
      defaultUnit = '/ semester';
    } else if (t === 'annual' || t === 'year' || t === 'per year') {
      defaultUnit = '/ year';
    } else if (t === 'one time' || t === 'one_time') {
      defaultUnit = '(one-time)';
    } else if (t === 'month' || t === 'monthly' || t === 'per month') {
      defaultUnit = '/ month';
    } else if (t === 'total' || t === 'total program') {
      defaultUnit = '(total)';
    } else {
      defaultUnit = `/ ${t}`;
    }
  }

  // Check if string already contains an inline unit/frequency suffix (e.g. "/ semester", "per credit hour")
  const unitMatch = str.match(/\s*(?:per|\/)\s*([a-zA-Z\s_-]+)$/i);
  let inlineUnit = '';
  let cleanStr = str;
  if (unitMatch) {
    inlineUnit = `/ ${unitMatch[1].trim()}`;
    cleanStr = str.substring(0, unitMatch.index).trim();
  }

  const finalUnit = inlineUnit ? inlineUnit : defaultUnit;

  // Check for range: e.g. "10,000 - 20,000", "10000 - 20000", "10,000 to 20,000"
  const rangeMatch = cleanStr.match(/^([^\d]*?)(\d[\d,.]*)\s*(?:-|–|—|\bto\b)\s*([^\d]*?)(\d[\d,.]*)(.*)$/i);
  if (rangeMatch) {
    const rawNum1 = parseFloat(rangeMatch[2].replace(/,/g, ''));
    const rawNum2 = parseFloat(rangeMatch[4].replace(/,/g, ''));

    if (!isNaN(rawNum1) && !isNaN(rawNum2)) {
      const num1Str = rawNum1.toLocaleString('en-US');
      const num2Str = rawNum2.toLocaleString('en-US');
      const suffix = finalUnit ? ` PKR ${finalUnit}` : ' PKR';
      return `${num1Str} - ${num2Str}${suffix}`.replace(/\s+/g, ' ').trim();
    }
  }

  // Single number check: e.g. "600,000", "600000 PKR", "Rs. 25,000", 25000
  const withoutCurrency = cleanStr.replace(/[PKR\s,Rs.]/gi, '');
  const feeNumber = Number(withoutCurrency);
  if (!isNaN(feeNumber) && withoutCurrency !== '') {
    const numStr = feeNumber.toLocaleString('en-US');
    const suffix = finalUnit ? ` PKR ${finalUnit}` : ' PKR';
    return `${numStr}${suffix}`.replace(/\s+/g, ' ').trim();
  }

  // If there are digits in the string (e.g. "25,000 (One-Time)"), preserve clean user string
  if (/\d/.test(str)) {
    return str;
  }

  return '—';
};

/**
 * Format program name consistently - remove degree prefix and use full name.
 * @param {string} programName - Program name (may include degree in parentheses)
 * @param {string} degreeType - Optional degree type to remove from name
 * @returns {string} Clean program name without degree prefix
 */
export const formatProgramName = (programName, degreeType = null) => {
  if (!programName) {
    return '';
  }

  // Remove degree prefix in parentheses like (BS), (MS), (PhD)
  let cleanName = programName.replace(/^\s*\([A-Za-z.]+\)\s*/, '');

  // Remove standalone degree prefix like "BS ", "MS ", "PhD "
  cleanName = cleanName.replace(/^(BS|MS|PhD|MBA|BBA|MBBS|BE|BSc|MSc|MA|BA)\s+/i, '');

  // Remove leading "in" prefix if it exists (e.g. "in Electrical Engineering")
  cleanName = cleanName.replace(/^in\s+/i, '');

  // Trim whitespace
  cleanName = cleanName.trim();

  // Handle special abbreviations
  const specialCases = {
    'cs': 'Computer Science',
    'se': 'Software Engineering',
    'ai': 'Artificial Intelligence',
    'ds': 'Data Science',
    'it': 'Information Technology',
    'ee': 'Electrical Engineering',
    'me': 'Mechanical Engineering',
    'ce': 'Civil Engineering'
  };

  const lowerName = cleanName.toLowerCase();
  if (specialCases[lowerName]) {
    return specialCases[lowerName];
  }

  // Return with proper capitalization
  return cleanName;
};

/**
 * Format program for display with clean, consistent prefix casing and parentheses normalization.
 * Respects authoritative program names (canonical or university-specific) without
 * guessing or forcefully prepending degree prefixes.
 *
 * @param {string} programName - Program name
 * @param {string} [degreeType] - Optional degree type (retained for signature compatibility)
 * @returns {string} Formatted display name
 */
export const formatProgramDisplay = (programName, degreeType = null) => {
  if (!programName || !programName.trim()) {
    return '';
  }

  const trimmed = programName.trim();
  const upper = trimmed.toUpperCase();

  // Standalone recognized qualification acronyms
  if (['BBA', 'MBA', 'MBBS', 'BDS', 'DPT', 'LLB', 'LLM'].includes(upper)) {
    return upper;
  }
  if (upper === 'PHARM.D' || upper === 'PHARMD') {
    return 'Pharm.D';
  }

  // Normalize parentheses around major: e.g. "BS (Computer Engineering)" -> "BS Computer Engineering"
  let cleanName = trimmed.replace(/^(BS|BSc|BE|B\.E\.?|B\.S\.?|BBA|MBA|MS|MSc|ME|M\.E\.?|MPhil|M\.Phil\.?|PhD|Ph\.D\.?)\s*\(([^)]+)\)$/i, '$1 $2');
  cleanName = cleanName.replace(/^\(([A-Za-z.]+)\)\s*(.+)$/, '$1 $2');

  // If programName already starts with a recognized degree prefix, normalize its canonical casing
  const DEGREE_PREFIX_REGEX = /^(BS|BSc|BE|B\.E\.?|B\.S\.?|BBA|MBA|MS|MSc|ME|M\.E\.?|MPhil|M\.Phil\.?|PhD|Ph\.D\.?|MBBS|BDS|Pharm\.?D|DPT|LLB|LLM|B\.Arch|BArch|ADP|PGD)\b/i;
  const match = cleanName.match(DEGREE_PREFIX_REGEX);

  if (match) {
    const pfx = match[1];
    let rest = cleanName.slice(match[0].length).trim();

    // Canonical prefix casing
    let canonPfx = pfx;
    const pfxUpper = pfx.toUpperCase().replace(/\./g, '');
    if (pfxUpper === 'BS') canonPfx = 'BS';
    else if (pfxUpper === 'BE') canonPfx = 'BE';
    else if (pfxUpper === 'BBA') canonPfx = 'BBA';
    else if (pfxUpper === 'MBA') canonPfx = 'MBA';
    else if (pfxUpper === 'MS') canonPfx = 'MS';
    else if (pfxUpper === 'ME') canonPfx = 'ME';
    else if (pfxUpper === 'MPHIL') canonPfx = 'MPhil';
    else if (pfxUpper === 'PHD') canonPfx = 'PhD';
    else if (pfxUpper === 'BSC') canonPfx = 'BSc';
    else if (pfxUpper === 'MSC') canonPfx = 'MSc';
    else if (pfxUpper === 'BARCH') canonPfx = 'B.Arch';
    else if (pfxUpper === 'ADP') canonPfx = 'ADP';
    else if (pfxUpper === 'PGD') canonPfx = 'PGD';

    rest = rest.replace(/^in\s+/i, '').trim();
    if (!rest) return canonPfx;

    // Avoid duplicating "Business Administration" if prefix is BBA or MBA
    if ((canonPfx === 'BBA' || canonPfx === 'MBA') && /^business administration$/i.test(rest)) {
      return canonPfx;
    }

    return `${canonPfx} ${rest}`;
  }

  // Return authoritative name directly without guessing or prepending BS/MS
  return cleanName;
};

/**
 * Parse fee input for form submission (remove formatting).
 * @param {string|number} feeInput - User input
 * @returns {number|null} Clean numeric value or null
 */
export const parseFeeForSubmission = (feeInput) => {
  if (feeInput === null || feeInput === undefined || feeInput === '' || 
      String(feeInput).trim().toLowerCase() === 'contact') {
    return null;
  }

  try {
    // Remove 'PKR', 'Rs', commas, and spaces
    const cleanValue = String(feeInput).replace(/[PKR\s,Rs.]/gi, '');
    if (!cleanValue) {
      return null;
    }
    const number = parseFloat(cleanValue);
    return isNaN(number) ? null : number;
  } catch (error) {
    return null;
  }
};

/**
 * Format date string consistently to "25 Jul 2026".
 * @param {string|Date} dateVal - Date value to format
 * @returns {string} Formatted date (e.g. '25 Jul 2026') or N/A
 */
export const formatDateDisplay = (dateVal) => {
  if (!dateVal) return 'N/A';
  try {
    const date = new Date(dateVal);
    if (isNaN(date.getTime())) return dateVal;

    const day = date.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    return String(dateVal);
  }
};

/**
 * Format date string into a relative time string (e.g. 'just now', '3 days ago', '2 weeks ago').
 * @param {string|Date} dateVal - Date value to format
 * @returns {string} Relative time string (e.g. '3 days ago', 'just now')
 */
export const formatRelativeTime = (dateVal) => {
  if (!dateVal) return '';
  try {
    let dateStr = String(dateVal).trim();
    // If ISO string without timezone indicator (e.g. 2026-09-10T12:00:00), treat as UTC
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(dateStr)) {
      dateStr += 'Z';
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return formatDateDisplay(dateVal);

    const now = new Date();
    // Compare calendar dates (midnight to midnight)
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfTarget = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffInDays = Math.round((startOfToday - startOfTarget) / (1000 * 60 * 60 * 24));

    if (diffInDays <= 0) {
      return 'today';
    }
    if (diffInDays === 1) {
      return 'yesterday';
    }
    if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInDays < 30) {
      return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    }

    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
  } catch (error) {
    return formatDateDisplay(dateVal);
  }
};


/**
 * Compute dynamic admission deadline status (Open / Closed / Upcoming).
 * Automatically resolves to 'Closed' if the application deadline date has passed.
 *
 * @param {string|Date|null} deadlineVal - The application deadline
 * @param {string|null} rawStatus - Stored or selected status ('Open', 'Closed', 'Upcoming')
 * @returns {'Open'|'Closed'|'Upcoming'|'TBA'} Dynamic deadline status
 */
export const computeDynamicDeadlineStatus = (deadlineVal, rawStatus = 'Open') => {
  if (deadlineVal) {
    try {
      const parsedDate = new Date(deadlineVal);
      if (!isNaN(parsedDate.getTime())) {
        // End of that deadline day (23:59:59.999 local time)
        const endOfDay = new Date(parsedDate);
        endOfDay.setHours(23, 59, 59, 999);

        if (new Date() > endOfDay) {
          return 'Closed';
        }
      }
    } catch (e) {
      // If parsing fails, fall back to explicit status
    }
  }

  if (rawStatus) {
    const clean = String(rawStatus).trim();
    const lower = clean.toLowerCase();
    if (lower === 'closed') return 'Closed';
    if (lower === 'upcoming') return 'Upcoming';
    if (lower === 'open') return 'Open';
  }

  return deadlineVal ? 'Open' : 'TBA';
};

/**
 * Shorten a URL for display as a text link.
 * @param {string} url - The URL to shorten
 * @param {string} fallback - Fallback label if URL is null
 * @returns {string} Shortened url label (e.g. habib.edu.pk/.../fee-structure)
 */
export const getShortSourceLabel = (url, fallback = 'Source') => {
  if (!url) return fallback;
  try {
    // Remove protocol and www
    let clean = url.replace(/^(https?:\/\/)?(www\.)?/i, '');
    // Remove trailing slash
    if (clean.endsWith('/')) {
      clean = clean.slice(0, -1);
    }
    if (clean.length > 35) {
      const parts = clean.split('/');
      if (parts.length > 2) {
        const domain = parts[0];
        const lastPart = parts[parts.length - 1];
        return `${domain}/.../${lastPart}`;
      }
      return clean.substring(0, 32) + '...';
    }
    return clean;
  } catch (e) {
    return fallback;
  }
};

/**
 * Format fee type for clean user display.
 * @param {string} ft - Fee type key (e.g. 'semester', 'credit_hour', 'annual', 'total')
 * @returns {string} Human-readable fee type label
 */
export const formatFeeType = (ft) => {
  if (!ft) return '';
  const map = {
    semester: 'Per Semester',
    per_semester: 'Per Semester',
    'per semester': 'Per Semester',
    credit_hour: 'Per Credit Hour',
    'credit hour': 'Per Credit Hour',
    per_credit_hour: 'Per Credit Hour',
    'per credit hour': 'Per Credit Hour',
    course: 'Per Course',
    per_course: 'Per Course',
    'per course': 'Per Course',
    annual: 'Annual / Per Year',
    year: 'Annual / Per Year',
    per_year: 'Annual / Per Year',
    'per year': 'Annual / Per Year',
    one_time: 'One-Time',
    'one time': 'One-Time',
    total: 'Total Program',
    'total program': 'Total Program',
    monthly: 'Per Month',
    'per month': 'Per Month',
  };
  const key = String(ft).toLowerCase().trim();
  return map[key] || ft;
};

/**
 * Standardized fee frequency options.
 */
export const FEE_FREQUENCY_OPTIONS = [
  { value: 'semester', label: 'Per Semester' },
  { value: 'credit_hour', label: 'Per Credit Hour' },
  { value: 'course', label: 'Per Course' },
  { value: 'annual', label: 'Annual / Per Year' },
  { value: 'one_time', label: 'One-Time' },
  { value: 'total', label: 'Total Program' },
  { value: 'monthly', label: 'Per Month' },
];

/**
 * Parses a raw fee entry into 3 clean parts: category, amount, frequency.
 * @param {Object} fee - Fee item from state or API
 * @returns {{ category: string, amount: string, frequency: string }}
 */
export const parseFeeStructureItem = (fee) => {
  if (!fee) return { category: '', amount: '', frequency: 'semester' };
  const category = fee.category || fee.name || '';
  let rawAmount = String(fee.amount || '').trim();
  let frequency = fee.frequency || fee.fee_type || '';

  if (!frequency && rawAmount) {
    if (/credit\s*hour/i.test(rawAmount)) {
      frequency = 'credit_hour';
    } else if (/\bcourse\b/i.test(rawAmount)) {
      frequency = 'course';
    } else if (/semester/i.test(rawAmount)) {
      frequency = 'semester';
    } else if (/(year|annual)/i.test(rawAmount)) {
      frequency = 'annual';
    } else if (/month/i.test(rawAmount)) {
      frequency = 'monthly';
    } else if (/one[\s_-]?time/i.test(rawAmount)) {
      frequency = 'one_time';
    } else if (/total/i.test(rawAmount)) {
      frequency = 'total';
    }
  }

  let cleanAmount = rawAmount;
  if (frequency && rawAmount) {
    cleanAmount = cleanAmount
      .replace(/\s*(?:\/|\bper\b)\s*([a-zA-Z\s_-]+)$/i, '')
      .replace(/\s*\((?:one[\s_-]?time|total)\)/i, '')
      .replace(/[PKR|Rs\.?]/gi, '')
      .trim();
  }

  return {
    category,
    amount: cleanAmount || rawAmount,
    frequency: frequency || 'semester'
  };
};

/**
 * Standardized intake session options (e.g. Fall 2026, Spring 2027).
 * Preserves existing custom value if not present in default presets.
 * @param {string} [currentValue] - Current session value
 * @returns {string[]} List of session option strings
 */
export const getIntakeSessionOptions = (currentValue) => {
  const currentYear = new Date().getFullYear();
  const presets = [
    `Fall ${currentYear}`,
    `Spring ${currentYear + 1}`,
    `Summer ${currentYear + 1}`,
    `Fall ${currentYear + 1}`,
    `Spring ${currentYear}`,
    `Summer ${currentYear}`,
    `Fall ${currentYear - 1}`,
    `Spring ${currentYear - 1}`,
    'Fall',
    'Spring',
    'Summer',
  ];
  if (currentValue && typeof currentValue === 'string' && currentValue.trim()) {
    const trimmed = currentValue.trim();
    if (!presets.includes(trimmed)) {
      return [trimmed, ...presets];
    }
  }
  return presets;
};

/**
 * Standardized list of degree levels across Dakhla.
 */
export const STANDARD_DEGREE_LEVELS = [
  'Bachelor',
  'Master',
  'Doctorate',
  'Associate Degree',
  'Postgraduate Diploma',
  'Diploma',
  'Certificate',
];

/**
 * Standardized degree level options.
 * Preserves existing custom/scraped value if not in default presets.
 * @param {string} [currentValue] - Current degree value
 * @param {Array<{name: string}>|string[]} [extraTypes] - Optional extra degree types
 * @returns {string[]} List of degree level option strings
 */
export const getDegreeLevelOptions = (currentValue, extraTypes = []) => {
  const presets = [...STANDARD_DEGREE_LEVELS];

  if (Array.isArray(extraTypes)) {
    extraTypes.forEach(t => {
      const name = typeof t === 'string' ? t.trim() : (t?.name || '').trim();
      if (name && !presets.some(p => p.toLowerCase() === name.toLowerCase())) {
        presets.push(name);
      }
    });
  }

  if (currentValue && typeof currentValue === 'string' && currentValue.trim()) {
    const trimmed = currentValue.trim();
    if (!presets.some(p => p.toLowerCase() === trimmed.toLowerCase())) {
      return [trimmed, ...presets];
    }
  }
  return presets;
};

/**
 * Standardized program duration options across Dakhla (including half-year periods).
 */
export const STANDARD_DURATIONS = [
  '0.5 year',
  '1 year',
  '1.5 years',
  '2 years',
  '2.5 years',
  '3 years',
  '3.5 years',
  '4 years',
  '4.5 years',
  '5 years',
  '5.5 years',
  '6 years',
];

/**
 * Format duration number/string nicely (e.g. 1.5 -> '1.5 years', 1 -> '1 year', 4.0 -> '4 years')
 */
export const formatDuration = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const num = Number(val);
  if (!isNaN(num) && typeof val === 'number') {
    const numStr = Number.isInteger(num) ? String(num) : String(num);
    return num === 1 ? `${numStr} year` : `${numStr} years`;
  }
  const str = String(val).trim();
  const parsed = parseFloat(str);
  if (!isNaN(parsed) && /^\d+(\.\d+)?$/.test(str)) {
    return parsed === 1 ? `${parsed} year` : `${parsed} years`;
  }
  return str;
};

/**
 * Format duration years for canonical catalog display (e.g. 1 -> '1 Year', 1.5 -> '1.5 Years')
 */
export const formatDurationYears = (durationYears) => {
  if (durationYears === null || durationYears === undefined || durationYears === '') return '4 Years';
  const num = Number(durationYears);
  if (isNaN(num)) return `${durationYears} Years`;
  return num === 1 ? '1 Year' : `${num} Years`;
};

/**
 * Standardized duration options.
 * Preserves existing custom/scraped value if not in default presets.
 * @param {string} [currentValue] - Current duration value
 * @returns {string[]} List of duration option strings
 */
export const getDurationOptions = (currentValue) => {
  const presets = [...STANDARD_DURATIONS];
  if (currentValue && typeof currentValue === 'string' && currentValue.trim()) {
    const trimmed = currentValue.trim();
    if (!presets.some(p => p.toLowerCase() === trimmed.toLowerCase())) {
      return [trimmed, ...presets];
    }
  }
  return presets;
};

/**
 * Standard degree group tiers for organizing university program offerings.
 */
export const DEGREE_GROUPS = [
  {
    key: 'undergraduate',
    title: 'Undergraduate',
  },
  {
    key: 'graduate',
    title: 'Graduate',
  },
  {
    key: 'doctoral',
    title: 'Doctoral (PhD)',
  },
  {
    key: 'associate',
    title: 'Associate Degree',
  },
  {
    key: 'diploma',
    title: 'Diplomas & Certificates',
  },
  {
    key: 'other',
    title: 'Other',
  },
];

/**
 * Classifies a program into its standard degree group key.
 * @param {Object} program - Program object (may contain degree, degree_type, name, raw_name, canonical_program)
 * @returns {'undergraduate'|'graduate'|'doctoral'|'associate'|'diploma'|'other'}
 */
export const getProgramDegreeGroup = (program) => {
  if (!program) return 'other';

  const deg = (
    (typeof program.degree === 'string' ? program.degree : '') ||
    (typeof program.degree_type === 'string' ? program.degree_type : '') ||
    (program.degree_type?.name || '') ||
    (program.canonical_program?.degree_type?.name || '') ||
    ''
  ).trim().toLowerCase();

  const name = (
    program.name ||
    program.program_name ||
    program.canonical_name ||
    program.raw_name ||
    ''
  ).trim().toLowerCase();

  const combined = `${deg} ${name}`.replace(/[^a-z0-9]/g, ' ');
  const words = new Set(combined.split(/\s+/).filter(Boolean));

  // 1. Doctoral Check first
  const isDoctoral =
    deg === 'doctorate' ||
    deg === 'doctoral' ||
    deg.includes('phd') ||
    deg.includes('ph.d') ||
    words.has('phd') ||
    words.has('dba') ||
    words.has('dphil') ||
    words.has('dsc') ||
    name.includes('ph.d') ||
    name.includes('phd') ||
    name.startsWith('doctor of philosophy');

  if (isDoctoral) {
    return 'doctoral';
  }

  // 2. Associate Degree / ADP Check (before Bachelor)
  const isAssociate =
    deg === 'associate degree' ||
    deg === 'associate' ||
    deg.includes('associate') ||
    deg.includes('adp') ||
    words.has('adp') ||
    words.has('ada') ||
    words.has('ads') ||
    name.includes('associate degree') ||
    name.startsWith('adp ') ||
    name.startsWith('adp(') ||
    name.startsWith('ada ') ||
    name.startsWith('ads ');

  if (isAssociate) {
    return 'associate';
  }

  // 3. Diplomas & Certificates
  const isDiploma =
    deg.includes('diploma') ||
    deg.includes('certificate') ||
    deg.includes('pgd') ||
    words.has('pgd') ||
    words.has('diploma') ||
    words.has('certificate') ||
    name.includes('postgraduate diploma') ||
    name.startsWith('diploma in') ||
    name.startsWith('certificate in');

  if (isDiploma) {
    return 'diploma';
  }

  // 4. Graduate / Master's Check
  const isGraduate =
    deg === 'master' ||
    deg.includes('master') ||
    deg.includes('postgrad') ||
    deg.includes('graduate') ||
    ['ms', 'msc', 'me', 'mba', 'emba', 'mphil', 'llm', 'mcs', 'mcom', 'meng'].some(w => words.has(w)) ||
    ['ms ', 'm.s.', 'msc ', 'm.sc.', 'mba ', 'm.b.a.', 'mphil ', 'm.phil.', 'llm ', 'l.l.m.'].some(p => name.startsWith(p));

  if (isGraduate) {
    return 'graduate';
  }

  // 5. Undergraduate / Bachelor's Check
  // In Pakistan, MBBS, BDS, Pharm-D, DPT are 5-year undergraduate professional programs
  const isUndergrad =
    deg === 'bachelor' ||
    deg.includes('bachelor') ||
    deg.includes('undergrad') ||
    ['bs', 'bsc', 'be', 'bba', 'bds', 'mbbs', 'dpt', 'barch', 'llb', 'bdes', 'bcs', 'bcom', 'ba'].some(w => words.has(w)) ||
    ['bs ', 'b.s.', 'bsc ', 'b.sc.', 'be ', 'b.e.', 'bba ', 'b.b.a.', 'mbbs', 'bds', 'dpt', 'pharm-d', 'pharmd', 'barch', 'llb', 'bdes'].some(p => name.startsWith(p)) ||
    name.includes('pharm-d') ||
    name.includes('pharm d') ||
    name.includes('doctor of pharmacy') ||
    name.includes('doctor of physical therapy');

  if (isUndergrad) {
    return 'undergraduate';
  }

  // Fallback if degree type had partial keywords
  if (deg) {
    if (deg.includes('bs') || deg.includes('be')) return 'undergraduate';
    if (deg.includes('ms') || deg.includes('mphil')) return 'graduate';
  }

  return 'other';
};


